import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import CmsBanner from "../../components/Banner/CmsBanner";
import { REGEX } from "../../utils/constant";
import { createInquiryAPI } from "../../api/user";
import useToast from "../../hooks/useToast";
import { useTranslation } from "react-i18next";

export default function ContactUs() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      const body = document.body;
      if (window.innerWidth >= 2400) {
        body.classList.add("scale-wrapper");
      } else {
        body.classList.remove("scale-wrapper");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const contactUsValidationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required(t("validation.nameRequired"))
      .min(2, t("validation.nameMin"))
      .max(50, t("validation.nameMax")),
    email: Yup.string()
      .matches(REGEX.EMAIL, t("validation.emailValid"))
      .required(t("validation.emailRequired")),
    message: Yup.string()
      .trim()
      .required(t("validation.messageRequired"))
      .min(10, t("validation.messageMin"))
      .max(500, t("validation.messageMax")),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createInquiryAPI,
    onSuccess: () => {
      useToast(t("messages.messageSent"), "success");
      formik.resetForm();
    },
    onError: (error: Error) => {
      console.error("Contact Us Error:", error.message);
      useToast(error.message || t("messages.failedToSend"), "error");
    },
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      message: "",
    },
    validationSchema: contactUsValidationSchema,
    onSubmit: (values) => {
      mutate(values);
    },
  });

  return (
    <div className="wrapper bg-primary-ivory min-h-screen">
      <CmsBanner
        classNameShape="hidden"
        title={t("contactUs.contactUs")}
        description={t("contactUs.weAreHere")}
      />

      <section className="2xl:py-[3.5vw] 2xl:pb-[8vw] lg:py-12 lg:pb-28 py-8 pb-20 relative z-10 bg-primary-ivory">
        <div className="w-full 2xl:max-w-[42vw] max-w-[650px] mx-auto px-4 relative z-20">
          <div className="bg-[#FFFDF7] lg:border-[0.13vw] border-2 border-primary-orange 2xl:rounded-[1.04vw] rounded-2xl 2xl:p-[2.2vw] lg:p-8 p-6 2xl:shadow-[0.313vw_0.313vw_0px_0px_#D76A4D] shadow-[6px_6px_0px_0px_#D76A4D]">
            {/* Header Title & Subtitle */}
            <div className="text-center 2xl:mb-[1vw] mb-4">
              <h2 className="2xl:text-[2vw]/tight lg:text-3xl/tight text-2xl/tight font-bold text-primary-orange mb-1">
                {t("contactUs.contactUs")}
              </h2>
              <p className="2xl:text-[0.93vw]/[1vw] md:text-lg/tight text-base/tight text-black-800 font-bold">
                {t("contactUs.weAreHere")}
              </p>
            </div>

            <hr className="border-0 2xl:h-[0.053vw] h-[1px] w-full 2xl:mb-[1.2vw] md:mb-5 mb-4 [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />

            <form onSubmit={formik.handleSubmit}>
              {/* Full Name Field */}
              <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
                <div
                  className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
                    2xl:rounded-[0.26vw] rounded-[5px]
                    skew-x-[-10deg]
                    2xl:border-[0.104vw] border-[1.5px]
                    transition-all
                    ${
                      formik.touched.name && formik.errors.name
                        ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
                        : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                    }
                  `}
                >
                  <input
                    type="text"
                    name="name"
                    placeholder={t("contactUs.enterFullName")}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isPending}
                    className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
                  />
                </div>
                {formik.touched.name && formik.errors.name && (
                  <p className="text-red-500 lg:text-[0.729vw] text-md lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold text-start">
                    {formik.errors.name}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
                <div
                  className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
                    2xl:rounded-[0.26vw] rounded-[5px]
                    skew-x-[-10deg]
                    2xl:border-[0.104vw] border-[1.5px]
                    transition-all
                    ${
                      formik.touched.email && formik.errors.email
                        ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
                        : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                    }
                  `}
                >
                  <input
                    type="text"
                    name="email"
                    placeholder={t("contactUs.enterEmail")}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isPending}
                    className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 lg:text-[0.729vw] text-md lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold text-start">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              {/* Message Field */}
              <div className="lg:mb-[1.3vw] md:mb-6 mb-5">
                <div
                  className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
                    2xl:rounded-[0.26vw] rounded-[5px]
                    skew-x-[-10deg]
                    2xl:border-[0.104vw] border-[1.5px]
                    transition-all
                    ${
                      formik.touched.message && formik.errors.message
                        ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
                        : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                    }
                  `}
                >
                  <textarea
                    name="message"
                    placeholder={t("contactUs.writeDescription")}
                    rows={4}
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isPending}
                    className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent resize-none hide-scroll"
                  />
                </div>
                {formik.touched.message && formik.errors.message && (
                  <p className="text-red-500 lg:text-[0.729vw] text-md lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold text-start">
                    {formik.errors.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isPending}
                className="w-full 2xl:rounded-[0.26vw] rounded-[5px] flex skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
              >
                <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
                  <span className="skew-x-[10deg] block">
                    {isPending ? (
                      <span className="flex items-center justify-center lg:gap-[0.417vw] gap-2">
                        <svg
                          className="animate-spin lg:w-[1.042vw] lg:h-[1.042vw] h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t("contactUs.sending")}
                      </span>
                    ) : (
                      t("contactUs.submit")
                    )}
                  </span>
                </span>
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
